import { useState, useRef, useEffect } from "react";
import { useGoogleLogin, googleLogout } from '@react-oauth/google';
import { useAuthStore } from '../store/auth';
import { verifyGoogleToken, logout as apiLogout } from '../api/client';

export default function AuthButton() {
  const { user, setUser } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        if (tokenResponse.access_token) {
          const userProfile = await verifyGoogleToken(tokenResponse.access_token);
          setUser(userProfile);
        }
      } catch (error) {
        console.error("Authentication failed:", error);
      }
    },
    onError: (error) => console.error('Login Failed', error)
  });

  const handleLogout = async () => {
    try {
      await apiLogout();
    } catch (e) {
      console.error("Logout failed:", e);
    } finally {
      googleLogout();
      setUser(null);
      setIsOpen(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  if (user) {
    return (
      <div className="relative" ref={dropdownRef}>
        {/* Profile Picture Trigger */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-center focus:outline-none cursor-pointer"
        >
          {user.picture ? (
            <img
              src={user.picture}
              alt="Profile"
              className="size-8 rounded-full border border-card-border hover:border-white/40 transition-colors"
            />
          ) : (
            <div className="size-8 rounded-full border border-card-border bg-card-bg flex items-center justify-center text-white hover:border-white/40 transition-colors">
              <span className="material-symbols-outlined text-[18px]">person</span>
            </div>
          )}
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute top-full right-0 mt-2 z-50 flex flex-col w-48 bg-card-dark border border-card-border rounded-xl shadow-2xl overflow-hidden animate-fade-in origin-top-right">
            <div className="p-3 border-b border-card-border">
              <p className="text-xs font-semibold text-white truncate">{user.name}</p>
              <p className="text-[10px] text-text-secondary truncate">{user.email}</p>
            </div>
            <div className="p-1">
              <button
                onClick={handleLogout}
                className="w-full text-left px-3 py-2 text-sm text-red-500 hover:bg-white/5 rounded-lg transition-colors flex items-center gap-2 cursor-pointer"
              >
                <span className="material-symbols-outlined text-[18px]">logout</span>
                Sign out
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      onClick={() => login()}
      className="flex items-center gap-1.5 text-sm font-medium text-white hover:text-primary transition-colors cursor-pointer bg-transparent border-none p-0"
    >
      <span className="material-symbols-outlined text-[20px] text-primary">person</span>
      Sign In
    </button>
  );
}
